/**
 * Convert tasks between different formats (YAML <-> JSON)
 */

import { Command } from 'commander';
import path from 'path';
import fs from 'fs-extra';
import yaml from 'js-yaml';
import chalk from 'chalk';
import ora from 'ora';
import { UnifiedTaskService } from '../adapters/unified-task-service.js';
import { GongfuAdapter, type GongfuTask } from '../adapters/gongfu-adapter.js';
import { WorkflowAdapter } from '../adapters/workflow-adapter.js';
import type { Task } from '../types/index.js';

export function createConvertCommand(): Command {
  const command = new Command('convert');
  
  command
    .description('Convert tasks between Gongfu YAML and Workflow JSON formats')
    .option('-f, --from <format>', 'Source format (yaml|json)', 'yaml')
    .option('-t, --to <format>', 'Target format (yaml|json)', 'json')
    .option('-i, --input <path>', 'Input file or directory')
    .option('-o, --output <path>', 'Output file or directory')
    .option('--sync', 'Sync tasks between both formats')
    .option('--dry-run', 'Show what would be converted without making changes')
    .action(async (options) => {
      await convertTasks(options);
    });
  
  command
    .command('sync')
    .description('Synchronize tasks between Gongfu and Workflow formats')
    .option('--dry-run', 'Show what would be synced without making changes')
    .action(async (options) => {
      await syncTasks(options);
    });
  
  return command;
}

async function convertTasks(options: any): Promise<void> {
  const spinner = ora('Converting tasks...').start();
  
  try {
    // Validate formats
    if (!['yaml', 'json'].includes(options.from) || !['yaml', 'json'].includes(options.to)) {
      throw new Error('Invalid format. Use "yaml" or "json"');
    }
    
    if (options.from === options.to) {
      throw new Error('Source and target formats must be different');
    }
    
    // Determine input and output paths
    // NOTE: .gongfu/task is deprecated, using .workflow for all formats
    const inputPath = options.input || '.workflow/tasks.json';
    const outputPath = options.output || '.workflow/tasks.json';
    
    // Check if input exists
    if (!await fs.exists(inputPath)) {
      throw new Error(`Input path not found: ${inputPath}`);
    }
    
    const stats = await fs.stat(inputPath);
    let convertedCount = 0;
    
    if (stats.isDirectory() && options.from === 'yaml') {
      // Convert multiple YAML files to JSON
      const files = await fs.readdir(inputPath);
      const yamlFiles = files.filter(f => f.endsWith('.yaml') || f.endsWith('.yml'));
      
      const tasks: Task[] = [];
      const gongfuAdapter = new GongfuAdapter();
      const workflowAdapter = new WorkflowAdapter();
      
      for (const file of yamlFiles) {
        const content = await fs.readFile(path.join(inputPath, file), 'utf-8');
        const gongfuTask = yaml.load(content) as GongfuTask;
        
        if (gongfuTask && gongfuTask.id) {
          const unified = gongfuAdapter.toUnified(gongfuTask);
          const workflowTask = workflowAdapter.fromUnified(unified);
          tasks.push(workflowTask);
          convertedCount++;
        }
      }
      
      if (!options.dryRun) {
        await fs.ensureDir(path.dirname(outputPath));
        await fs.writeJson(outputPath, { tasks }, { spaces: 2 });
      }
      
      spinner.succeed(`Converted ${convertedCount} tasks from YAML to JSON`);
      
    } else if (stats.isFile() && options.from === 'json') {
      // Convert JSON to multiple YAML files
      const data = await fs.readJson(inputPath);
      const tasks = data.tasks || [];
      
      const gongfuAdapter = new GongfuAdapter();
      const workflowAdapter = new WorkflowAdapter();
      
      if (!options.dryRun) {
        await fs.ensureDir(outputPath);
      }
      
      for (const task of tasks) {
        const unified = workflowAdapter.toUnified(task);
        const gongfuTask = gongfuAdapter.fromUnified(unified);
        
        const yamlContent = yaml.dump(gongfuTask, {
          indent: 2,
          lineWidth: -1,
          noRefs: true,
          sortKeys: false,
        });
        
        if (!options.dryRun) {
          const outputFile = path.join(outputPath, `${gongfuTask.id}.yaml`);
          await fs.writeFile(outputFile, yamlContent, 'utf-8');
        }
        
        convertedCount++;
      }
      
      spinner.succeed(`Converted ${convertedCount} tasks from JSON to YAML`);
      
    } else {
      throw new Error('Invalid input format or path');
    }
    
    if (options.dryRun) {
      console.log(chalk.yellow('\n⚠️  Dry run mode - no files were actually created'));
    }
    
  } catch (error: any) {
    spinner.fail(chalk.red(error.message));
    process.exit(1);
  }
}

async function syncTasks(options: any): Promise<void> {
  const spinner = ora('Synchronizing tasks...').start();
  
  try {
    const service = new UnifiedTaskService({
      autoSync: !options.dryRun,
    });
    
    if (options.dryRun) {
      // Just show what would be synced
      const allTasks = await service.getAllTasks();
      spinner.info(`Found ${allTasks.length} tasks to sync`);
      
      console.log('\nTasks by source:');
      const workflowTasks = allTasks.filter(t => t.metadata.source === 'workflow');
      const gongfuTasks = allTasks.filter(t => t.metadata.source === 'gongfu');
      
      console.log(chalk.blue(`  Workflow: ${workflowTasks.length} tasks`));
      console.log(chalk.green(`  Gongfu: ${gongfuTasks.length} tasks`));
      
      console.log(chalk.yellow('\n⚠️  Dry run mode - no synchronization performed'));
    } else {
      const result = await service.syncTasks();
      
      spinner.succeed(`Synchronized ${result.synced} tasks`);
      
      if (result.conflicts.length > 0) {
        console.log(chalk.yellow(`\n⚠️  ${result.conflicts.length} conflicts found:`));
        result.conflicts.forEach(task => {
          console.log(chalk.yellow(`  - ${task.id}: ${task.title}`));
        });
      }
    }
    
  } catch (error: any) {
    spinner.fail(chalk.red(error.message));
    process.exit(1);
  }
}

// Export for testing
export { convertTasks, syncTasks };